// src/ecrans/operations/operations-calculs.js — Opérations NEST : la
// journée de l'équipe depuis `ecran_journee_lire { jours: 2 }` (pur, testé).

import { heureH } from "../accueil/accueil-calculs";
import { pl } from "../nest";

const HEURE_DEPART_DEFAUT = "11:00";
const HEURE_ARRIVEE_DEFAUT = "16:00";

/** « 11:00 » → minutes depuis minuit ; null si illisible. */
export function minutes(h) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(h || ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Tuiles : ménages faits / total, départs, arrivées. */
export function tuilesJour(jour) {
  if (!jour) return null;
  const menages = jour.menages || [];
  const heures = (liste) => {
    const hs = [...new Set(liste.map((s) => heureH(s.heure)).filter(Boolean))];
    return hs;
  };
  const resume = (liste) => {
    const hs = heures(liste);
    if (!hs.length) return null;
    const sans = liste.filter((s) => !s.heure).length;
    const tete = hs.slice(0, 2).join(", ");
    const reste = Math.max(0, hs.length - 2) + sans;
    return reste > 0 ? `${tete} et ${pl(reste, "autre")}` : tete;
  };
  return {
    faits: menages.filter((m) => m.fini).length,
    total: menages.length,
    departs: (jour.departs || []).length,
    departsHeures: resume(jour.departs || []),
    arrivees: (jour.arrivees || []).length,
    arriveesHeures: resume(jour.arrivees || []),
  };
}

/**
 * Les lignes de la frise d'un jour, par logement : le départ, le ménage,
 * l'arrivée du même logement ce jour-là. Groupes de la maquette :
 * « À régler » (personne dessus, ou deux ménages au même endroit),
 * « À venir », « Validés » (finis).
 */
export function friseDuJour(jour) {
  if (!jour) return { aRegler: [], aVenir: [], valides: [] };
  const menages = jour.menages || [];
  const parLog = new Map();
  for (const m of menages) {
    const id = m.logement ? m.logement.id : `inconnu-${m.id}`;
    if (!parLog.has(id)) parLog.set(id, []);
    parLog.get(id).push(m);
  }
  const lignes = menages.map((m) => {
    const id = m.logement ? m.logement.id : null;
    const depart = (jour.departs || []).find(
      (d) => (m.reservation_id && d.id === m.reservation_id) || (id && d.logement && d.logement.id === id),
    );
    const arrivee = (jour.arrivees || []).find((a) => id && a.logement && a.logement.id === id);
    const debut = minutes(m.heure) ?? minutes((depart && depart.heure) || HEURE_DEPART_DEFAUT);
    const duree = Number.isFinite(m.duree_min) ? m.duree_min : null;
    const double = id ? parLog.get(id).length > 1 : false;
    let raison = null;
    if (!m.fini && !m.agent) raison = arrivee ? "personne dessus : à confier" : "personne dessus";
    else if (!m.fini && double) raison = "deux ménages en même temps";
    return {
      menage: m,
      nom: m.logement ? m.logement.nom : "Logement inconnu",
      depart: depart
        ? { qui: depart.voyageur, h: minutes(depart.heure || HEURE_DEPART_DEFAUT), heureDite: Boolean(depart.heure) }
        : null,
      arrivee: arrivee
        ? { qui: arrivee.voyageur, h: minutes(arrivee.heure || HEURE_ARRIVEE_DEFAUT), heureDite: Boolean(arrivee.heure) }
        : null,
      debut,
      fin: duree !== null && debut !== null ? debut + duree : null,
      raison,
    };
  });
  return {
    aRegler: lignes.filter((l) => l.raison),
    aVenir: lignes.filter((l) => !l.raison && !l.menage.fini),
    valides: lignes.filter((l) => l.menage.fini),
  };
}

/** Le résumé « Demain » : un ménage par logement, avec départ et contrôle. */
export function demain(jour) {
  if (!jour) return null;
  const menages = jour.menages || [];
  return {
    date: jour.date,
    nb: menages.length,
    sansAgent: menages.filter((m) => !m.agent).length,
    lignes: menages.map((m) => {
      const id = m.logement ? m.logement.id : null;
      const depart = (jour.departs || []).find(
        (d) => (m.reservation_id && d.id === m.reservation_id) || (id && d.logement && d.logement.id === id),
      );
      return {
        id: m.id,
        nom: m.logement ? m.logement.nom : "Logement inconnu",
        depart: depart ? { qui: depart.voyageur, heure: heureH(depart.heure) } : null,
        agent: m.agent ? m.agent.prenom : null,
        controleur: m.controleur ? m.controleur.prenom : null,
      };
    }),
  };
}

/**
 * « Il en faut maintenant » (porte `ecran_stock_a_apporter_lire`), ménages du
 * jour et manques en retard. Sans prix, comme la porte.
 */
export function lignesStock(stock) {
  if (!stock) return [];
  const nomP = (p) => p.nom || p.texte || "Produit inconnu";
  const ligne = (cle, logement, produits, agent) => {
    const urgent = produits.filter((p) => p.pour_cette_arrivee);
    const liste = (urgent.length ? urgent : produits).map(nomP).join(", ");
    const quand = urgent.length ? "il manque pour cette arrivée" : "il manquera au prochain ménage";
    return {
      cle,
      nom: logement ? logement.nom : "Logement inconnu",
      texte: `${quand} : ${liste}${agent ? ` · ${agent}` : ""}`,
    };
  };
  return [
    ...(stock.menages || []).map((m) => ligne(`m:${m.menage_id}`, m.logement, m.produits, m.agent && m.agent.prenom)),
    ...(stock.en_retard || []).map((g, i) => ({
      cle: `r:${i}`,
      nom: g.logement ? g.logement.nom : "Logement inconnu",
      texte: `en retard : ${g.produits.map(nomP).join(", ")}`,
    })),
  ];
}

/** Une tâche de la porte → sa ligne (sous-titre « 31 juillet · Entreprise »). */
const PORTEE = { logement: "Logement", proprio: "Propriétaire", proprietaire: "Propriétaire", entreprise: "Entreprise", divers: "Divers" };
export function sousTache(t, dateCourte) {
  return [
    t.heure ? t.heure.replace(":", "h") : null,
    t.echeance && dateCourte ? dateCourte(t.echeance) : null,
    t.logement ? t.logement.nom : PORTEE[t.portee] || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Les notes regroupées par portée, dans l'ordre de la maquette. */
export function notesParPortee(notes) {
  const ordre = ["logement", "proprio", "entreprise", "divers"];
  const g = new Map(ordre.map((k) => [k, []]));
  for (const n of notes || []) {
    const p = n.portee === "proprietaire" ? "proprio" : n.portee;
    const k = ordre.includes(p) ? p : "divers";
    g.get(k).push(n);
  }
  return ordre.map((k) => ({ portee: k, libelle: PORTEE[k], notes: g.get(k) })).filter((x) => x.notes.length);
}
